import { Position } from 'src/common-services/types/geojson-types'
import { BaseClient } from 'src/common-services/clients/base-client'
import {
  BrandExampleMap,
  TenantFinderDemographicsAggregation,
  TenantFinderDemographicsHistogram,
} from 'src/common-services/types/tenant-findet-types'
import { BrandContactInfo, PersonContactInfo, VoidMeasure } from 'src/common-services/types/void-types'
import {
  TenantFinderBrandsContactsRequest,
  TenantFinderBrandListMap,
  TenantFinderBrandsContactInfo,
  TenantFinderCategoryListMap,
  TenantFinderDetailsRequest,
  TenantFinderDetailsResponse,
  TenantFinderRecommendationsSummary,
  TenantFinderRecommendationsSummaryRequest,
  TenantFinderRequest,
  TenantFinderResponse,
  TenantFinderRecommendedCoTenancyRequest,
  TenantFinderRecommendedCoTenancyResponse,
  TenantFinderAllBrandsContactInfo,
  TenantFinderBrandLocationInfo,
  TenantFinderBrandLocationExtraInfo,
} from 'src/types/tenant-finder'

export class TenantFinderClient extends BaseClient {
  getHistogramDemographics(
    metric: VoidMeasure['key'],
    buckets: number = 1000
  ): Promise<TenantFinderDemographicsHistogram> {
    return this.get(`/tenant-finder/histograms/demographics/${metric}`, { buckets })
  }
  getAggregationsDemographics(metric: VoidMeasure['key']): Promise<TenantFinderDemographicsAggregation> {
    return this.get(`/tenant-finder/aggregations/demographics/${metric}`)
  }
  getExampleBrands(brandIds: string[], radiusMiles: number): Promise<BrandExampleMap> {
    return this.post('/tenant-finder/examples/brands', { radiusMiles, brandIds })
  }

  // Experimental
  getExperimentalByPoint(request: TenantFinderRequest): Promise<TenantFinderResponse> {
    return this.post('/tenant-finder/by-point', request)
  }

  getExperimentalByPointDetails(request: TenantFinderDetailsRequest): Promise<TenantFinderDetailsResponse> {
    return this.post('/tenant-finder/by-point/details', request)
  }

  getExperimentalByPointSummary(
    request: TenantFinderRecommendationsSummaryRequest
  ): Promise<TenantFinderRecommendationsSummary> {
    return this.post('/tenant-finder/by-point/summary', request)
  }

  getExperimentalByPointRecommendedCoTenancy(
    request: TenantFinderRecommendedCoTenancyRequest
  ): Promise<TenantFinderRecommendedCoTenancyResponse> {
    return this.post('/tenant-finder/by-point/recommended-cotenancy', request)
  }

  getExperimentalBrandsContacts(request: TenantFinderBrandsContactsRequest): Promise<TenantFinderBrandsContactInfo> {
    return this.post('/tenant-finder/exp-contacts', request)
  }

  getAllBrandsContacts(request: TenantFinderBrandsContactsRequest): Promise<TenantFinderAllBrandsContactInfo> {
    return this.post('/tenant-finder/contacts', request)
  }

  getExperimentalBrands(): Promise<TenantFinderBrandListMap> {
    return this.get('/tenant-finder/brands')
  }

  getExperimentalCategories(): Promise<TenantFinderCategoryListMap> {
    return this.get('/tenant-finder/categories')
  }

  getAreaMetrics(point: Position, radiusMiles: number): Promise<Record<string, number>> {
    return this.get('/tenant-finder/area-metrics', { point, radiusMiles })
  }

  getPersonContactInfo(id: string): Promise<PersonContactInfo[]> {
    return this.get(`/tenant-finder/contact-info/${encodeURIComponent(id)}`)
  }

  getBrandContactInfo(id: string): Promise<BrandContactInfo[]> {
    return this.get(`/tenant-finder/brand-contact-info/${encodeURIComponent(id)}`)
  }

  getBrandLocationsInfo(brandName: string): Promise<TenantFinderBrandLocationInfo[]> {
    return this.get(`/tenant-finder/brand-locations-info/${encodeURIComponent(brandName)}`)
  }

  getBrandLocationsExtraInfo(brandName: string): Promise<TenantFinderBrandLocationExtraInfo[]> {
    return this.get(`/tenant-finder/brand-locations-info/${encodeURIComponent(brandName)}/enrich`)
  }
}
